import React from 'react'
import { Editor, EditorState, RichUtils, Modifier } from 'draft-js'
import { selectionIsLink, getEntityAtCursor } from 'lib/draftUtils'
import * as blockUtils from 'lib/blockUtils'

// Draft editor component that encapsulates all of our custom selection
// handling logic.

export class CustomDraftEditor extends React.PureComponent {
  handleKeyCommand = (command) => {
    const currentBlockType = RichUtils.getCurrentBlockType(this.props.editorState)
    // disallow inline styles for headings etc
    if (!['unstyled', 'unordered-list-item'].includes(currentBlockType)) {
      return 'not-handled'
    }

    // don't remove atomic blocks on (backspace|delete) / allow removal of empty text blocks succeeding atomic blocks
    if (command === 'backspace' || command === 'backspace-word' || command === 'backspace-to-start-of-line' ||
        command === 'delete' || command === 'delete-word') {
      const { editorState } = this.props
      const content = editorState.getCurrentContent()
      const selection = editorState.getSelection()
      const currentKey = selection.getFocusKey()
      const currentBlock = content.getBlockForKey(currentKey)
      const keyBefore = content.getKeyBefore(currentKey)
      const blockBefore = content.getBlockForKey(keyBefore)
      const blockAfter = content.getBlockAfter(currentKey)

      const isBackspaceAndAtStart = (command === 'backspace' ||
                                     command === 'backspace-word' ||
                                     command === 'backspace-to-start-of-line') && selection.getFocusOffset() === 0
      const isDeleteAndAtEnd = (command === 'delete' || command === 'delete-word') && selection.getFocusOffset() === currentBlock.getLength()
      const blockBeforeIsAtomic = blockBefore && blockBefore.getType() === 'atomic'
      const blockAfterIsAtomic = blockAfter && blockAfter.getType() === 'atomic'

      // TODO put this somewhere else. This allows us to backspace and delete an <hr/> present in the GuidePageEditor
      const canDelete = blockBefore && blockBefore.getData().get('type') === 'separator'

      if (((isBackspaceAndAtStart && blockBeforeIsAtomic) ||
           (isDeleteAndAtEnd && blockAfterIsAtomic)
      ) && selection.isCollapsed() && !canDelete) {
        // if the current block is empty and isn't the last block, remove it on backspace/delete
        if (currentBlock.getLength() === 0 && blockAfter) {
          const selectionToRemove = selection.merge({
            focusKey: keyBefore,
            isBackward: true
          })
          const newContent = Modifier.removeRange(content, selectionToRemove, 'backward')
          const newEditorState = EditorState.push(editorState, newContent, 'remove-range')
          // this.blurEditor()
          this.onChange(newEditorState)
        }
        return 'handled'
      }
    }

    const newState = RichUtils.handleKeyCommand(this.props.editorState, command)
    if (newState) {
      this.onChange(newState)
      return 'handled'
    }

    return 'not-handled'
  }

  handleReturn = (e) => {
    const breakout = blockUtils.blockBreakout(this.props.editorState)
    if (breakout) {
      this.onChange(breakout)
      return 'handled'
    }
    return 'not-handled'
  }

  handleBeforeInput = (chars) => {
    // if the user inputs a space character at the end of a link, break out of
    // the link.
    const { editorState } = this.props
    const selection = editorState.getSelection()
    if (chars === ' ' && selection.isCollapsed() && selectionIsLink(editorState)) {
      const trailingEntity = getEntityAtCursor(editorState, 'trailing')
      if (trailingEntity && trailingEntity.getType() === 'LINK') {
        return 'not-handled'
      }
      const newContent = Modifier.insertText(
        editorState.getCurrentContent(),
        editorState.getSelection(),
        chars,
        null,
        null
      )
      this.onChange(EditorState.push(editorState, newContent, 'insert-characters'))
      return 'handled'
    }
    return 'not-handled'
  }

  onChange = (newEditorState, cb) => {
    const { onChange } = this.props
    // Draft onchange doesn't account for atomic blocks that care about selection state,
    // so we need to forceSelection when an atomic block loses focus
    const oldEditorState = this.props.editorState
    const oldSelection = oldEditorState.getSelection().getStartKey()
    if ((newEditorState.getSelection().getStartKey() !== oldSelection) && oldEditorState.getCurrentContent().getBlockForKey(oldSelection).getType() === 'atomic') {
      const newNewEditorState = EditorState.forceSelection(
        newEditorState,
        newEditorState.getSelection()
      )
      onChange(newNewEditorState, cb)
      return
    }

    onChange(newEditorState, cb)
  }

  focus = () => {
    this._editor.focus()
  }

  blur = () => {
    this._editor.blur()
  }

  exitCurrentMode = () => {
    this._editor.exitCurrentMode()
  }

  ref = editor => {
    this._editor = editor
  }

  // todo what about focusEditor?
  render () {
    const { editorState, placeholder, blockRendererFn, blockStyleFn, blockRenderMap } = this.props

    return <Editor
      spellCheck
      ref={this.ref}
      handleKeyCommand={this.handleKeyCommand}
      handleReturn={this.handleReturn}
      handleBeforeInput={this.handleBeforeInput}
      blockStyleFn={blockStyleFn}
      editorState={editorState}
      onChange={this.onChange}
      blockRendererFn={blockRendererFn}
      blockRenderMap={blockRenderMap}
      placeholder={placeholder} />
  }
}
